/**
 * Phase 45 Plan 45-05 — KPI band composer.
 *
 * 4 cards in a 4-column grid per UI-SPEC §Surface 1 KPI card spec:
 *   1. Active projects   value=count; sub="{N} at risk · {N} over budget"
 *   2. Hours logged      value="{N}h"; sub="{N}% of budget"; accent={pct, color}
 *   3. Revenue           value="{N} Kč"; sub="{N}h billable"
 *   4. Avg margin        value="{N}%"; sub="on billable projects"
 *
 * Deltas: when `delta === null` (no prev range / no data) the card renders
 * "—" instead of a fake number per UI-SPEC §Specifics.
 *
 * MKS-328: the revenue figure is CZK and is formatted as CZK. The Avg margin
 * value is revenue-weighted on the backend — nothing about that changes here.
 */
import { ManagerKpiCard, type DeltaTone } from './ManagerKpiCard';
import { formatPortfolioMoney } from './money';
import { MoneyMetricLabel } from '../common/MoneyMetricLabel';
import type { PortfolioKpis, PortfolioRow } from '../../services/api/portfolio';
import { LEGACY_EN_NUMBER_LOCALE, formatNumber } from '../../utils/localeFormat';

export interface ManagerOverviewKpiBandProps {
  kpis: PortfolioKpis;
  projects: PortfolioRow[];
}

function formatCount(value: number): string {
  return formatNumber(Math.round(value), undefined, LEGACY_EN_NUMBER_LOCALE);
}

function formatHours(value: number): string {
  const rounded = Math.round(value);
  return `${formatNumber(rounded, undefined, LEGACY_EN_NUMBER_LOCALE)}h`;
}

/** MKS-328 — the figure IS CZK, so the shared CZK money formatter renders it. */
function formatRevenue(value: number): string {
  return formatPortfolioMoney(value);
}

function formatPercent(value: number | null): string {
  if (value === null || value === undefined) return '—';
  return `${Math.round(value * 100)}%`;
}

function deltaTone(delta: number | null, higherIsBetter = true): DeltaTone {
  if (delta === null || delta === undefined || delta === 0) return 'neutral';
  if (higherIsBetter) return delta > 0 ? 'good' : 'bad';
  return delta > 0 ? 'bad' : 'good';
}

function formatDelta(
  delta: number | null,
  format: 'count' | 'hours' | 'currency' | 'percent',
): string | null {
  if (delta === null || delta === undefined) return null;
  const sign = delta > 0 ? '+' : '';
  if (format === 'count') return `${sign}${Math.round(delta)} vs prev`;
  if (format === 'hours') return `${sign}${Math.round(delta)}h vs prev`;
  if (format === 'currency') {
    // A negative delta carries its own '-' through the formatter.
    return `${sign}${formatPortfolioMoney(delta)} vs prev`;
  }
  return `${sign}${Math.round(delta * 100)}% vs prev`;
}

export function ManagerOverviewKpiBand({ kpis, projects }: ManagerOverviewKpiBandProps) {
  // Derive at-risk / over-budget counts from the projects array for the
  // Active projects sub-text. Backend pre-computes the headline `value`
  // and `sub` strings already; we use the prepared `kpis.activeProjects.sub`
  // verbatim (defensive: fall back to a freshly computed sub if missing).
  const atRiskCount = projects.filter((p) => p.status === 'At risk').length;
  const overBudget = projects.filter((p) => p.burnPct > 1).length;
  const activeSub =
    kpis.activeProjects.sub ?? `${atRiskCount} at risk · ${overBudget} over budget`;

  const hoursPct = kpis.hoursLogged.pct ?? 0;
  const hoursAccent =
    kpis.hoursLogged.budget !== null
      ? {
          pct: Math.min(100, hoursPct),
          color: hoursPct > 100 ? '#ef4444' : '#10b981',
        }
      : null;
  const hoursSub =
    kpis.hoursLogged.budget !== null
      ? `${Math.round(hoursPct)}% of budget`
      : 'No budget set';

  // MKS-332 — the Avg-margin tile is absent for anyone who is not admin or
  // finance (the API drops the whole `avgMargin` block), so the band drops to
  // three columns rather than leaving a gap where the tile was. Both class
  // strings are literal so Tailwind's JIT emits them.
  const avgMargin = kpis.avgMargin;
  const bandClass = avgMargin
    ? 'grid grid-cols-4 gap-3 mb-5'
    : 'grid grid-cols-3 gap-3 mb-5';

  return (
    <div className={bandClass}>
      <ManagerKpiCard
        label="Active projects"
        value={formatCount(kpis.activeProjects.value)}
        sub={activeSub}
        delta={formatDelta(kpis.activeProjects.delta, 'count')}
        deltaTone={kpis.activeProjects.deltaTone}
      />
      <ManagerKpiCard
        label="Hours logged"
        value={formatHours(kpis.hoursLogged.value)}
        sub={hoursSub}
        delta={formatDelta(kpis.hoursLogged.delta, 'hours')}
        deltaTone={deltaTone(kpis.hoursLogged.delta)}
        accent={hoursAccent}
      />
      <ManagerKpiCard
        // MKS-332 follow-up: the tile said "Revenue" while the column below it
        // said "Earned" — the same number under two names on one screen, which
        // is the exact drift this ticket exists to end. Both read Earned, from
        // the same glossary entry.
        label={<MoneyMetricLabel metric="earned" />}
        value={formatRevenue(kpis.revenue.value)}
        sub={`${formatHours(kpis.revenue.billableHours)} billable`}
        delta={formatDelta(kpis.revenue.delta, 'currency')}
        deltaTone={deltaTone(kpis.revenue.delta)}
      />
      {avgMargin && (
        <ManagerKpiCard
          label={<MoneyMetricLabel metric="margin" label="Avg margin" />}
          value={formatPercent(avgMargin.value)}
          sub="on billable projects"
          delta={formatDelta(avgMargin.delta, 'percent')}
          deltaTone={deltaTone(avgMargin.delta)}
        />
      )}
    </div>
  );
}
